export interface AboutVersionSource {
  releaseVersion?: string | null;
  buildCommit?: string | null;
  engineVersion?: string | null;
}

export interface AboutVersionDetails {
  releaseVersion: string;
  buildCommit: string;
  engineVersion: string;
  buildLabel: string;
  engineLabel: string;
}

const SEMVER_PATTERN = /^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/u;
const RUNTIME_SEMVER_PATTERN = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/u;

export function parseAboutVersionResponse(value: unknown): AboutVersionSource {
  if (!value || typeof value !== 'object') {
    return { releaseVersion: null, buildCommit: null };
  }
  const response = value as Record<string, unknown>;
  const releaseVersion =
    typeof response.releaseVersion === 'string' &&
    RUNTIME_SEMVER_PATTERN.test(response.releaseVersion.trim())
      ? response.releaseVersion.trim()
      : null;
  const buildCommit =
    typeof response.buildCommit === 'string' && response.buildCommit.trim()
      ? response.buildCommit.trim()
      : null;
  return { releaseVersion, buildCommit };
}

/**
 * Narrow consumer boundary for the shared-version pipeline (NR-1).
 * Missing values stay explicit; this module never derives an engine version.
 */
export function getAboutVersionDetails(
  source: AboutVersionSource = {},
  language: 'de' | 'en' = 'en'
): AboutVersionDetails {
  const legacyVersion = BUILD_CONFIG.appVersion;
  const hasRuntimeReleaseVersion = Object.hasOwn(source, 'releaseVersion');
  const releaseVersion = hasRuntimeReleaseVersion
    ? source.releaseVersion
    : (BUILD_CONFIG.releaseVersion ??
      (SEMVER_PATTERN.test(legacyVersion) ? legacyVersion : null));
  const buildCommit =
    source.buildCommit ??
    BUILD_CONFIG.buildCommit ??
    globalThis.SENTRY_RELEASE?.id ??
    (!SEMVER_PATTERN.test(legacyVersion) ? legacyVersion : null);
  const engineVersion = source.engineVersion ?? BUILD_CONFIG.editorVersion;

  const unavailable = language === 'de' ? 'Nicht verfügbar' : 'Not available';
  return {
    releaseVersion: releaseVersion || unavailable,
    buildCommit: buildCommit || unavailable,
    engineVersion: engineVersion || unavailable,
    buildLabel: 'Build / commit',
    engineLabel: language === 'de' ? 'Engine-Version' : 'Engine version',
  };
}
